/**
 * Vertretung der Agentur im Vertrag (0071): Die Firmenangaben nennen eine oder mehrere vertretungsberechtigte Personen
 * (org_legal.representatives, die erste zugleich representative_name). Im Vertrag steht „vertreten durch“ mit den dafür
 * gewählten Personen (contracts.agency_representatives), ohne Wahl mit den ersten; beim Vier-Augen-Prinzip mit zweien
 * (Gesamtvertretung). Rein, getestet in packages/domain/test/contract-representatives.test.ts.
 */
import type { Language } from "./types.js";

export interface Representative {
  name: string;
  position?: string | null;
}

/** Namen getrimmt, leere und zu kurze raus, jeder Name einmal (die erste Nennung zählt), höchstens zehn (wie die Datenbank). */
export function cleanRepresentatives(list: readonly Partial<Representative>[] | null | undefined): Representative[] {
  const out: Representative[] = [];
  for (const r of list ?? []) {
    const name = (r?.name ?? "").trim().slice(0, 120);
    if (name.length < 2 || out.some((x) => x.name.toLowerCase() === name.toLowerCase())) continue;
    const position = (r?.position ?? "").trim().slice(0, 120);
    out.push(position ? { name, position } : { name });
    if (out.length === 10) break;
  }
  return out;
}

/**
 * Wer im Vertrag für die Agentur „vertreten durch“ genannt wird: die gewählten Personen (höchstens so viele, wie
 * gegenzeichnen), sonst die ersten der Firmenangaben. Unbekannte Namen der Wahl bleiben stehen (die Firmenangaben können
 * sich seit dem Entwurf geändert haben); die Prüfung vor dem Versand meldet sie über `unknownRepresentatives`.
 */
export function chosenRepresentatives(org: readonly Representative[], chosen: readonly Representative[] | null | undefined, agencySignatures: number): Representative[] {
  const n = Math.max(1, Math.min(2, Math.trunc(agencySignatures) || 1));
  const pick = cleanRepresentatives(chosen ?? []);
  if (pick.length) {
    // Funktion aus den Firmenangaben, wenn dort gepflegt (sie gilt), sonst die gespeicherte
    return pick.slice(0, n).map((p) => {
      const known = org.find((o) => o.name.trim().toLowerCase() === p.name.toLowerCase());
      return { name: known?.name ?? p.name, position: (known?.position ?? p.position ?? null) || null };
    });
  }
  return cleanRepresentatives(org).slice(0, n);
}

/** Gewählte Namen, die in den Firmenangaben nicht (mehr) als Vertretung stehen. */
export function unknownRepresentatives(org: readonly Representative[], chosen: readonly Representative[] | null | undefined): string[] {
  return cleanRepresentatives(chosen ?? []).filter((c) => !org.some((o) => o.name.trim().toLowerCase() === c.name.toLowerCase())).map((c) => c.name);
}

const JOINT: Record<Language, { undersigned: string; and: string }> = {
  de: { undersigned: "die unterzeichnenden vertretungsberechtigten Personen", and: "und" },
  en: { undersigned: "the undersigned authorised signatories", and: "and" },
};

/**
 * Text für „vertreten durch“ ({{agency.represented_by}}): „Name, Funktion“, bei zwei Personen „A, Funktion, und B,
 * Funktion“. Fehlt einer Person die Funktion, gibt es keinen Text (die Prüfung meldet die Lücke). Beim Vier-Augen-Prinzip
 * mit nur einer bekannten Person wie bisher „die unterzeichnenden vertretungsberechtigten Personen“.
 * `position` übersetzt die Funktion in die Vertragssprache (englische Verträge).
 */
export function representedByText(list: readonly Representative[], language: Language, opts: { fourEyes?: boolean; position?: (p: string, index: number) => string | null } = {}): string | null {
  const t = JOINT[language === "en" ? "en" : "de"];
  const people = cleanRepresentatives(list).slice(0, opts.fourEyes ? 2 : 1);
  if (opts.fourEyes && people.length < 2) return t.undersigned;
  if (!people.length) return null;
  const parts: string[] = [];
  for (const [i, p] of people.entries()) {
    const pos = p.position ? (opts.position ? opts.position(p.position, i) : p.position) : null;
    if (!pos) return null;
    parts.push(`${p.name}, ${pos}`);
  }
  return parts.length === 2 ? `${parts[0]}, ${t.and} ${parts[1]}` : parts[0]!;
}
